import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from "react";
import { FaChevronRight, FaChevronLeft, FaChevronDown } from "react-icons/fa";
import CloseIcon from "../images/white-close-icon.png";
import AppConstants from "../AppConstants";
import axios from "axios";

const MultiLevelSelect = ({ value = {}, onChange, reset }) => {
  const [data, setData] = useState([]);
  const [path, setPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [urlInitialized, setUrlInitialized] = useState(false);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Reset component state
  useEffect(() => {
    setPath([]);
    setSearchTerm("");
    setLoading(false);
    setUrlInitialized(false);
  }, [reset]);

  // Load initial professions data
  useEffect(() => {
    const loadProfessions = async () => {
      try {
        const res = await fetch(
          `${AppConstants.Jobs_API}/professions/getAllProfessions`
        );
        const json = await res.json();
        const professions = json.data.map((item) => ({
          name: item.profession,
          id: item._id,
          children: []
        }));
        setData(professions);
      } catch (e) {
        console.error("Failed to fetch professions", e);
      }
    };

    loadProfessions();
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Initialize from URL parameters (optimized version)
  useEffect(() => {
    const initializeFromUrlParams = async () => {
      if (
        urlInitialized ||
        !data.length ||
        !value.certification?.length ||
        !value.speciality?.length ||
        path.length > 0
      ) {
        return;
      }

      setUrlInitialized(true);
      setLoading(true);

      try {
        let matchedProfession = null;
        let matchedCertification = null;

        // Search through professions to find matching certification
        for (const profession of data) {
          let certifications = profession.children;

          // Load certifications if not already loaded
          if (certifications.length === 0) {
            const res = await axios.get(
              `${AppConstants.Jobs_API}/certification/getBullhornMatchedCertifications/${profession.id}`
            );
            certifications = res.data.data.map((cert) => ({
              name: cert.certification,
              id: cert._id,
              children: []
            }));
          }

          // Check for matching certification
          const foundCert = certifications.find((cert) =>
            value.certification.includes(cert.name)
          );

          if (foundCert) {
            matchedProfession = profession;
            matchedCertification = foundCert;

            // Update data with certifications and specialities
            const specsRes = await axios.get(
              `${AppConstants.Jobs_API}/specialities/getBullhornMatchedSpecialities/${foundCert.id}`
            );
            const specialities = specsRes.data.data.map((s) => s.speciality);

            setData((prevData) =>
              prevData.map((p) =>
                p.id === profession.id
                  ? {
                      ...p,
                      children: certifications.map((c) =>
                        c.id === foundCert.id
                          ? { ...c, children: specialities }
                          : c
                      )
                    }
                  : p
              )
            );

            setPath([matchedProfession.name, matchedCertification.name]);
            break;
          }
        }
      } catch (error) {
        console.error("Failed to initialize from URL params", error);
      } finally {
        setLoading(false);
      }
    };

    initializeFromUrlParams();
  }, [
    data,
    value.certification,
    value.speciality,
    path.length,
    urlInitialized
  ]);

  // Memoized current data calculation
  const currentData = useMemo(() => {
    return path.reduce((acc, item) => {
      const found = acc.find((i) => i.name === item);
      return found?.children || [];
    }, data);
  }, [data, path]);

  // Memoized final level check
  const isFinalLevel = useMemo(() => {
    return typeof currentData[0] === "string" || currentData.length === 0;
  }, [currentData]);

  // Memoized filtered data
  const filteredData = useMemo(() => {
    return currentData.filter((item) => {
      const itemName = typeof item === "string" ? item : item.name;
      return itemName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [currentData, searchTerm]);

  // API call handlers
  const handleProfessionClick = useCallback(
    async (profession) => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${AppConstants.Jobs_API}/certification/getBullhornMatchedCertifications/${profession.id}`
        );
        const certifications = res.data.data.map((cert) => ({
          name: cert.certification,
          id: cert._id,
          children: []
        }));

        setData((prevData) =>
          prevData.map((item) =>
            item.id === profession.id
              ? { ...item, children: certifications }
              : item
          )
        );

        setPath((prev) => [...prev, profession.name]);
        setSearchTerm("");
        onChange({
          profession: profession.name,
          certification: null,
          speciality: []
        });
      } catch (e) {
        console.error("Failed to fetch certifications", e);
      } finally {
        setLoading(false);
      }
    },
    [onChange]
  );

  const handleCertificationClick = useCallback(
    async (profession, certification) => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${AppConstants.Jobs_API}/specialities/getBullhornMatchedSpecialities/${certification.id}`
        );
        const specialities = res.data.data.map((s) => s.speciality);

        setData((prevData) =>
          prevData.map((p) =>
            p.id === profession.id
              ? {
                  ...p,
                  children: p.children.map((c) =>
                    c.id === certification.id
                      ? { ...c, children: specialities }
                      : c
                  )
                }
              : p
          )
        );

        setPath((prev) => [...prev, certification.name]);
        setSearchTerm("");
        onChange({
          profession: path[0],
          certification: [certification.name],
          speciality: []
        });
      } catch (e) {
        console.error("Failed to fetch specialities", e);
      } finally {
        setLoading(false);
      }
    },
    [onChange, path]
  );

  // Navigation handlers
  const handleNext = useCallback(
    async (name) => {
      const current = path.reduce((acc, item) => {
        const found = acc.find((i) => i.name === item);
        return found?.children || [];
      }, data);

      const selected = current.find((i) => i.name === name);
      if (!selected) return;

      if (path.length === 0) {
        await handleProfessionClick(selected);
      } else if (path.length === 1) {
        const selectedProfession = data.find((p) => p.name === path[0]);
        if (selectedProfession) {
          await handleCertificationClick(selectedProfession, selected);
        }
      } else {
        setPath((prev) => [...prev, name]);
      }
    },
    [data, path, handleProfessionClick, handleCertificationClick]
  );

  const handleBack = useCallback(() => {
    setPath((prev) => prev.slice(0, -1));
    setSearchTerm("");
  }, []);

  // Selection handlers
  const toggleSelect = useCallback(
    (item) => {
      const updated = value.speciality?.includes(item)
        ? value.speciality.filter((i) => i !== item)
        : [...(value.speciality || []), item];

      onChange({
        ...value,
        speciality: updated
      });
    },
    [value, onChange]
  );

  const handleSelectAll = useCallback(() => {
    const allSelected = filteredData.every((i) =>
      value.speciality?.includes(i)
    );
    const updated = allSelected
      ? value.speciality.filter((i) => !filteredData.includes(i))
      : [
          ...(value.speciality || []),
          ...filteredData.filter((i) => !value.speciality?.includes(i))
        ];

    onChange({ ...value, speciality: updated });
  }, [filteredData, value, onChange]);

  const removeTag = useCallback(
    (item) => {
      const updatedSpeciality = value.speciality.filter((i) => i !== item);
      onChange({
        ...value,
        speciality: updatedSpeciality
      });
    },
    [value, onChange]
  );

  // Event handlers
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const toggleDropdown = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  // Render helpers
  const renderMenuItem = useCallback(
    (item, idx) => (
      <li
        key={idx}
        className="mls-menu-item"
        onClick={() => handleNext(item.name)}
      >
        {item.name} <FaChevronRight />
      </li>
    ),
    [handleNext]
  );

  const renderCheckboxItem = useCallback(
    (item, idx) => (
      <li key={idx} className="mls-menu-item">
        <label>
          <input
            type="checkbox"
            checked={value.speciality?.includes(item)}
            onChange={() => toggleSelect(item)}
          />
          <span>{item}</span>
        </label>
      </li>
    ),
    [value.speciality, toggleSelect]
  );

  const renderSelectedTag = useCallback(
    (item) => (
      <span className="mls-tag selected-tags m-0" key={item}>
        <em>{item}</em>
        <img
          className="mls-close-icon"
          onClick={() => removeTag(item)}
          src={CloseIcon}
          alt="close"
        />
      </span>
    ),
    [removeTag]
  );

  return (
    <div
      className="multi-level-select custom_multilevel_select"
      ref={dropdownRef}
    >
      {/* Select input box */}
      <div className="mls-input-box" onClick={toggleDropdown}>
        <span className="mls-placeholder">
          {value.speciality?.length > 0
            ? `${value.speciality.length} selected`
            : "Select.."}
        </span>
        <FaChevronDown className={`mls-chevron ${isOpen ? "open" : ""}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="mls-dropdown">
          {/* Back button */}
          {path.length > 0 && (
            <div className="mls-back-btn" onClick={handleBack}>
              <FaChevronLeft /> Back to{" "}
              {path.length === 2 ? "Profession Type" : "Profession"}
            </div>
          )}

          {/* Search input */}
          <div className="mls-search-container">
            <input
              ref={searchInputRef}
              type="text"
              className="mls-search-input"
              placeholder={`Search ${
                path.length === 0
                  ? "Professions"
                  : path.length === 1
                  ? "Profession Types"
                  : "Specialties"
              }...`}
              value={searchTerm}
              onChange={handleSearchChange}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Menu container */}
          <div className="mls-menu-container">
            <ul className="mls-menu-list">
              {!isFinalLevel ? (
                filteredData.length > 0 ? (
                  filteredData.map(renderMenuItem)
                ) : (
                  <li className="mls-no-results">
                    No results found for "{searchTerm}"
                  </li>
                )
              ) : (
                <>
                  {/* Select All checkbox */}
                  {filteredData.length > 0 && (
                    <li className="mls-menu-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={filteredData.every((i) =>
                            value.speciality?.includes(i)
                          )}
                          onChange={handleSelectAll}
                        />
                        <span>Select All {searchTerm ? "Filtered" : ""}</span>
                      </label>
                    </li>
                  )}

                  {/* Individual items */}
                  {filteredData.length > 0 ? (
                    filteredData.map(renderCheckboxItem)
                  ) : (
                    <li className="mls-no-results">
                      No results found for "{searchTerm}"
                    </li>
                  )}
                </>
              )}
            </ul>
          </div>

          {/* Loading indicator */}
          {loading && <div className="mls-loading">Loading...</div>}
        </div>
      )}

      {/* Selected tags */}
      {value.speciality?.length > 0 && (
        <div
          className="mls-selected-tags"
          style={
            value.speciality.length > 5
              ? { height: "200px", overflowY: "scroll" }
              : {}
          }
        >
          <div className="mls-tag-list mt-2">
            {value.speciality.map(renderSelectedTag)}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLevelSelect;
